import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import CartPanel from './CartPanel';

const GAP_PX = 8;
const MIN_SIDE_PX = 8;
const MAX_WIDTH_PX = 420;

function computeStyle(anchorEl) {
    if (!anchorEl || typeof window === 'undefined') {
        return null;
    }
    const r = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let width = Math.min(MAX_WIDTH_PX, vw - 2 * MIN_SIDE_PX);
    let right = vw - r.right;
    const leftEdge = vw - right - width;
    if (leftEdge < MIN_SIDE_PX) {
        right = Math.max(MIN_SIDE_PX, vw - width - MIN_SIDE_PX);
    }
    const maxHeight = Math.max(200, vh - r.bottom - GAP_PX - MIN_SIDE_PX);
    return {
        position: 'fixed',
        top: r.bottom + GAP_PX,
        right,
        width,
        maxHeight,
        zIndex: 200,
    };
}

/**
 * Выпадающая корзина под кнопкой в шапке (anchorRef — обёртка кнопки «Корзина»).
 */
const CartModal = ({open, onClose, anchorRef}) => {
    const panelRef = useRef(null);
    const [style, setStyle] = useState(null);

    const syncPosition = useCallback(() => {
        const el = anchorRef?.current;
        if (!el) {
            return;
        }
        setStyle(computeStyle(el));
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (!open) {
            return undefined;
        }
        syncPosition();
        const onWin = () => syncPosition();
        window.addEventListener('resize', onWin);
        window.addEventListener('scroll', onWin, true);
        return () => {
            window.removeEventListener('resize', onWin);
            window.removeEventListener('scroll', onWin, true);
        };
    }, [open, syncPosition]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onDown = (e) => {
            const t = e.target;
            if (anchorRef?.current?.contains(t)) {
                return;
            }
            if (panelRef.current?.contains(t)) {
                return;
            }
            onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, onClose, anchorRef]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return (
        <div
            ref={panelRef}
            className="flex flex-col overflow-hidden rounded-2xl border border-stone-200/90 bg-white shadow-xl"
            style={style ?? undefined}
            role="dialog"
            aria-modal="false"
            aria-labelledby="cart-modal-title"
        >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4">
                <CartPanel mode="modal" onClose={onClose}/>
            </div>
        </div>
    );
};

export default CartModal;
